// Lightweight attribution capture utility.
//
// On every page load the marketing site checks the URL for marketing
// query params and known click ids; when present we persist them in
// localStorage so they survive across pages and outlive the visitor's
// browsing session up until they convert (lead-capture, registration,
// or booking). All conversion-time API payloads should hit
// `attachAttribution()` so the CRM's Sources/Funnel dashboards can
// attribute the conversion correctly.

const STORAGE_KEY = 'nala.attribution'

// URL params we copy verbatim. utm_* are the standard suspects; gclid
// is Google Ads' click id, fbclid is the Facebook equivalent.
const URL_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'fbclid',
] as const

// Synthesised keys we capture from the page itself, not the URL.
const SYNTH_KEYS = ['landing_page', 'referrer'] as const

const ALL_KEYS = [...URL_KEYS, ...SYNTH_KEYS] as const

export type AttributionParams = Partial<Record<(typeof ALL_KEYS)[number], string>>

// Back-compat alias for the original UTM-only type.
export type UtmParams = AttributionParams

export function captureUtmFromUrl(): AttributionParams {
  if (typeof window === 'undefined') return {}

  const url = new URL(window.location.href)
  const captured: AttributionParams = {}

  URL_KEYS.forEach((k) => {
    const v = url.searchParams.get(k)
    if (v) captured[k] = v
  })

  // Track the FIRST landing page + referrer the visitor arrived on, so
  // navigating around the site doesn't overwrite their original entry
  // point. Only stamp these on the first call when there's nothing in
  // storage yet OR when the URL has explicit attribution params (which
  // signals "this is a fresh paid-traffic landing").
  const existing = readStored()
  const isFreshLanding = Object.keys(captured).length > 0 || !existing.landing_page

  if (isFreshLanding) {
    captured.landing_page = url.pathname + url.search
    const referrer = document.referrer
    if (referrer && !referrer.startsWith(window.location.origin)) {
      captured.referrer = referrer
    }
  }

  if (Object.keys(captured).length > 0) {
    try {
      const merged = { ...existing, ...captured }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
    } catch {
      // Private mode / disabled storage — silently ignore.
    }
  }

  return getStoredUtm()
}

function readStored(): AttributionParams {
  if (typeof window === 'undefined') return {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw) as AttributionParams
    // One-off migration from the old utm-only storage key. Read once,
    // then it'll be overwritten on the next capture.
    const legacy = localStorage.getItem('nala.utm')
    return legacy ? (JSON.parse(legacy) as AttributionParams) : {}
  } catch {
    return {}
  }
}

export function getStoredUtm(): AttributionParams {
  return readStored()
}

export function getStoredAttribution(): AttributionParams {
  return readStored()
}

export function attachUtm<T extends Record<string, unknown>>(payload: T): T & AttributionParams {
  return { ...readStored(), ...payload }
}

export function attachAttribution<T extends Record<string, unknown>>(
  payload: T,
): T & AttributionParams {
  return { ...readStored(), ...payload }
}
